import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import {
    CountryModel,
    LocalizationService as GeneratedLocalizationService,
    TranslatedString,
} from 'lemunzo-api';
import { BehaviorSubject, filter, map } from 'rxjs';
import { CountryCode } from '../models/country-code';
import { LanguageCode } from '../models/language-code';
import { Translations } from '../models/translations';
import { ConstantsService } from './constants.service';

/**
 * Type representing valid normalization forms for the 'form' input of the String.prototype.normalize() method.
 *
 * @description
 * The `FormType` type restricts the values that can be used for the 'form' input
 * of the String.prototype.normalize method. Valid values are 'NFC', 'NFD', 'NFKC', and 'NFKD'.
 */
type FormType = 'NFC' | 'NFD' | 'NFKC' | 'NFKD';

@Injectable({
    providedIn: 'root',
})
export class LocalizationService {
    private readonly _defaultLanguage = new BehaviorSubject<
        LanguageCode | undefined
    >(undefined);

    /**
     * An observable value of the default language.
     */
    readonly defaultLanguage$ = this._defaultLanguage
        .asObservable()
        .pipe(filter((result) => !!result));

    get defaultLanguage() {
        return (
            this._defaultLanguage.getValue() ?? ConstantsService.DEFAULT_LANG
        );
    }

    private readonly _defaultCountry = new BehaviorSubject<
        CountryCode | undefined
    >(undefined);

    /**
     * An observable value of the default country.
     */
    readonly defaultCountry$ = this._defaultCountry
        .asObservable()
        .pipe(filter((result) => !!result));

    get defaultCountry() {
        return (
            this._defaultCountry.getValue() ?? ConstantsService.DEFAULT_COUNTRY
        );
    }

    private readonly _localizedCountries = new BehaviorSubject<
        Map<string, CountryModel[]>
    >(new Map<string, CountryModel[]>());

    /**
     * An observable collection of the localized countries.
     */
    readonly localizedCountries$ = this._localizedCountries.asObservable().pipe(
        filter(Boolean),
        map((countriesByLanguage) => {
            const supportedCountriesByLanguage = new Map<
                string,
                CountryModel[]
            >();
            for (const [language, countries] of countriesByLanguage.entries()) {
                supportedCountriesByLanguage.set(
                    language,
                    countries.filter((country) => country.isSupported)
                );
            }
            return supportedCountriesByLanguage;
        })
    );

    constructor(
        private readonly destroyRef: DestroyRef,
        private readonly localizationApi: GeneratedLocalizationService,
        private readonly translateService: TranslateService
    ) {}

    /**
     * Returns the current language code, using a fallback chain when needed.
     *
     * @returns The current language (TranslateService current → fallback → default language → ConstantsService default).
     */
    public getCurrentLanguage(): string {
        return (
            this.translateService.getCurrentLang() ??
            this.translateService.getFallbackLang() ??
            this.defaultLanguage ??
            ConstantsService.DEFAULT_LANG
        );
    }

    /**
     * Loads default language from the API.
     */
    getDefaultLanguage(): void {
        this.localizationApi
            .getDefaultLanguage()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this._defaultLanguage.next(result as LanguageCode);
                },
                error: (_err) => {
                    this._defaultLanguage.next(ConstantsService.DEFAULT_LANG);
                },
            });
    }

    /**
     * Loads default country from the API.
     */
    getDefaultCountry(): void {
        this.localizationApi
            .getDefaultCountry()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this._defaultCountry.next(result as CountryCode);
                },
                error: (_err) => {
                    this._defaultCountry.next(ConstantsService.DEFAULT_COUNTRY);
                },
            });
    }

    /**
     * Loads localizations of the countries from the API.
     */
    public loadLocalizedCountries(): void {
        this.localizationApi
            .getLocalizedCountries()
            .pipe(
                map(
                    (result) =>
                        new Map<string, CountryModel[]>(Object.entries(result))
                ),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (result) => {
                    this._localizedCountries.next(result);
                },
                error: (_err) => {
                    this._localizedCountries.next(
                        new Map<string, CountryModel[]>()
                    );
                },
            });
    }

    /**
     * Localizes country code by language code.
     * @param countryCode The country code to get localization for.
     * @param languageCode The language code to get localization for.
     * @returns Localized country name.
     */
    public getLocalizedCountryName(
        countryCode: string,
        languageCode: string
    ): string {
        const localizedCountries = this._localizedCountries
            .getValue()
            .get(languageCode);

        if (!localizedCountries) {
            return countryCode;
        }

        const country = localizedCountries.find((x) => x.code === countryCode);

        return country ? country.name : countryCode;
    }

    /**
     * Function to remove diacritics from a string using the specified normalization form.
     *
     * @param str - The input string from which diacritics will be removed.
     * @param [form='NFD'] The normalization form to be applied ('NFC', 'NFD', 'NFKC', or 'NFKD').
     * Defaults to 'NFD' if not provided.
     * @returns The string with diacritics removed.
     * @export
     *
     * @description
     * The `removeDiacritics` function takes an input string (`str`) and a normalization form (`form`)
     * and returns the string with diacritics removed. It first normalizes the string using the specified
     * form and then removes all diacritics using a regular expression.
     */
    public static removeDiacritics(str: string, form: FormType = 'NFD') {
        if (!str) return '';
        return str.normalize(form).replace(/[\u0300-\u036f]/g, ''); // removes all diacritics from the string
    }

    /**
     * Creates a `TranslatedString` with no translations.
     *
     * @returns A new `TranslatedString` with an empty `translations` map.
     */
    public static emptyTranslatedString(): TranslatedString {
        return { translations: {} };
    }

    /**
     * Creates a shallow copy of a `TranslatedString`.
     *
     * @param original - The `TranslatedString` to copy.
     * @returns A new `TranslatedString` with its own `translations` map
     */
    public static copyTranslatedString(
        original: TranslatedString
    ): TranslatedString {
        const translations: Translations = {};
        for (let translationsKey in original.translations) {
            translations[translationsKey] =
                original.translations[translationsKey];
        }
        return { translations: translations };
    }

    /**
     * Compares two `TranslatedString`s by value.
     *
     * @param a - The first `TranslatedString` to compare.
     * @param b - The second `TranslatedString` to compare.
     * @returns `true` when both hold the same translation for every language.
     */
    public static translatedStringsEqual(
        a: TranslatedString,
        b: TranslatedString
    ): boolean {
        const keys = new Set([
            ...Object.keys(a.translations),
            ...Object.keys(b.translations),
        ]);
        for (const key of keys) {
            if ((a.translations[key] ?? '') !== (b.translations[key] ?? '')) {
                return false;
            }
        }
        return true;
    }
}
